import { HugeiconsIcon } from '@hugeicons/react'
import { BedIcon, Building03Icon, Cancel01Icon, CrownIcon } from '@hugeicons/core-free-icons'
import { useState } from 'react'

import { useAppStore } from '@/store'
import { cn } from '@/lib/utils'
import { formatSliderTime } from '@/three/solar'

const RESIDENCES = [
  { index: null, label: 'All residences', icon: Building03Icon },
  { index: 1, label: '2 Bedroom', icon: BedIcon },
  { index: 2, label: '3 Bedroom', icon: BedIcon },
  { index: 3, label: 'Penthouse', icon: CrownIcon },
] as const

const GALLERY = [
  { src: '/img/interior-view.jpeg', label: 'Living' },
  { src: '/img/bedroom-view.jpeg', label: 'Bedroom' },
  { src: '/img/bathroom-view.jpeg', label: 'Bathroom' },
  { src: '/img/interior-2.jpeg', label: 'Interior' },
]

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  const closeTool = useAppStore((s) => s.closeTool)
  return (
    // glass + dropdown-surface together, the way Tower's DropdownPanel does it:
    // dropdown-surface alone only styles the dark theme, so in light mode the
    // panel had no background at all and floated unreadable over the scene.
    <div className="glass dropdown-surface pointer-events-auto fixed bottom-28 left-1/2 z-50 w-[min(22rem,calc(100vw-2rem))] -translate-x-1/2 p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-semibold">{title}</span>
        <button
          type="button"
          onClick={closeTool}
          aria-label="Close"
          className="text-foreground/50 hover:text-foreground rounded-control flex size-7 cursor-pointer items-center justify-center"
        >
          <HugeiconsIcon icon={Cancel01Icon} size={15} strokeWidth={2} />
        </button>
      </div>
      {children}
    </div>
  )
}

export function ToolPanels() {
  const tool = useAppStore((s) => s.tool)
  const residence = useAppStore((s) => s.residence)
  const setResidence = useAppStore((s) => s.setResidence)
  const timeOfDay = useAppStore((s) => s.timeOfDay)
  const setTimeOfDay = useAppStore((s) => s.setTimeOfDay)
  const liveTime = useAppStore((s) => s.liveTime)
  const setLiveTime = useAppStore((s) => s.setLiveTime)
  const [lightbox, setLightbox] = useState<string | null>(null)

  if (tool === 'residences') {
    return (
      <Panel title="Residences">
        <div className="flex flex-col gap-1">
          {RESIDENCES.map((r) => (
            <button
              key={r.label}
              type="button"
              onClick={() => setResidence(r.index)}
              aria-pressed={residence === r.index}
              className={cn(
                'rounded-control flex cursor-pointer items-center gap-2.5 px-3 py-2.5 text-sm transition-colors',
                residence === r.index
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground/70 hover:bg-foreground/5 hover:text-foreground',
              )}
            >
              <HugeiconsIcon icon={r.icon} size={16} strokeWidth={2} />
              {r.label}
            </button>
          ))}
        </div>
      </Panel>
    )
  }

  if (tool === 'time') {
    return (
      <Panel title="Time of day">
        <div className="flex flex-col gap-3">
          <div className="flex items-baseline justify-between">
            <span className="font-serif text-2xl tabular-nums">{formatSliderTime(timeOfDay)}</span>
            <button
              type="button"
              onClick={() => setLiveTime(!liveTime)}
              aria-pressed={liveTime}
              className={cn(
                'rounded-control cursor-pointer px-2.5 py-1 text-xs font-medium transition-colors',
                liveTime
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground/60 hover:text-foreground bg-foreground/5',
              )}
            >
              Live
            </button>
          </div>
          <input
            type="range"
            min={0}
            max={1}
            step={0.001}
            value={timeOfDay}
            onChange={(e) => setTimeOfDay(Number(e.target.value))}
            aria-label="Time of day"
            className="accent-primary w-full cursor-pointer"
          />
          <p className="text-muted-foreground text-xs">
            Real sun position for Diriyah, 05:00 to 23:00 local.
          </p>
        </div>
      </Panel>
    )
  }

  if (tool === 'interiors') {
    return (
      <>
        <Panel title="Interiors">
          <div className="grid grid-cols-2 gap-2">
            {GALLERY.map((g) => (
              <button
                key={g.src}
                type="button"
                onClick={() => setLightbox(g.src)}
                className="group relative aspect-4/3 cursor-pointer overflow-hidden rounded-lg"
              >
                <img
                  src={g.src}
                  alt={g.label}
                  className="size-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <span className="absolute inset-x-0 bottom-0 bg-linear-to-t from-black/70 to-transparent px-2 py-1.5 text-left text-xs font-medium text-white">
                  {g.label}
                </span>
              </button>
            ))}
          </div>
        </Panel>
        {lightbox && (
          <div
            className="fixed inset-0 z-100 flex items-center justify-center bg-black/80 p-6"
            onClick={() => setLightbox(null)}
          >
            <img src={lightbox} alt="" className="max-h-full max-w-full rounded-xl object-contain" />
          </div>
        )}
      </>
    )
  }

  return null
}
